
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, Prop, Ref, Vue } from 'vue-property-decorator';
import 'leaflet/dist/leaflet.css';
import 'leaflet.locatecontrol/dist/L.Control.Locate.css';
import * as L from 'leaflet';
import 'leaflet.locatecontrol';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet-easybutton';
import 'leaflet-easybutton/src/easy-button.css';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import 'leaflet.offline';
import '../plugins/leaflet-condensed-attribution/dist/leaflet-control-condensed-attribution.js';
import { LeafletMapConfig } from '../types/LeafletMapConfig';
import { useMachineStore } from '../stores/machineStore';
import { useMenuStore } from '../stores/menuStore';
import { useMissionsStore } from '../stores/missionsStore';

L.Marker.prototype.options.icon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow
});

@Component
export default class LeafletMapLoader extends Vue {
  @Ref() readonly mapRef!: HTMLDivElement;
  @Prop() readonly options!: LeafletMapConfig;

  public machineStore = useMachineStore();
  public menuStore = useMenuStore();
  public missionStore = useMissionsStore();
  readonly MIN_STORAGE_ZOOM_LEVEL = 14;
  readonly MAX_STORAGE_ZOOM_LEVEL = 20;
  ready = false;
  map = null as L.Map | null;
  defaultMapCenter = new L.LatLng(51.174066, -105.8017);

  initializeMap() {
    this.map = L.map(this.mapRef, this.options.mapOptions);

    this.map.setView(this.options.defaultMapCenter ? this.options.defaultMapCenter : this.defaultMapCenter, 14);

    this.map.whenReady(() => (this.ready = true));
    // L.tileLayer('https://api.mapbox.com/v4/{id}/{z}/{x}/{y}@2x.png?access_token={accessToken}', {
    //   attribution:
    //     'Map data (c) <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors, <a href="https://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>, Imagery (c) <a href="https://www.mapbox.com/">Mapbox</a>',
    //   maxZoom: 18,
    //   id: 'mapbox.satellite',
    //   accessToken: this.options.apiKey
    // }).addTo(this.map);

    //https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile
    //clarity.maptiles.arcgis.com/arcgis/rest/services/World_Imagery/MapServer/tile
    let layer;
    if (this.options.apiKey !== '') {
      layer = L.tileLayer.offline(
        'https://clarity.maptiles.arcgis.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        {
          accessToken: this.options.apiKey,
          maxNativeZoom: 17,
          maxZoom: 23,
          detectRetina: true,
          crossOrigin: true,
          attribution:
            'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
        }
      );
      layer.addTo(this.map);
    }

    this.map.attributionControl.remove();
    const attribution = this.options.condensedAttribution
      ? new (L.Control as any).CondensedAttribution()
      : new L.Control.Attribution();
    attribution.setPosition(
      this.options.attributionControlPosition ? this.options.attributionControlPosition : 'bottomright'
    );
    if (this.options.mapOptions.attributionControl || this.options.mapOptions.attributionControl == undefined)
      attribution.addTo(this.map);

    this.map.zoomControl?.setPosition(
      this.options.zoomControlPosition ? this.options.zoomControlPosition : 'bottomright'
    );

    this.map.setZoom(this.options.mapOptions.zoom ? this.options.mapOptions.zoom : 14);

    const zoomLevelsStorage = (map: L.Map) => {
      const range = (start: number, end: number) =>
        Array.from(Array(Math.ceil(end) + 1).keys()).slice(Math.floor(start));
      let zoom = this.MAX_STORAGE_ZOOM_LEVEL;
      if (map.getZoom() < zoom) zoom = map.getZoom() + 1;
      return range(this.MIN_STORAGE_ZOOM_LEVEL, zoom);
    };

    const confirm = function (layer: any, successCallback: any) {
      // eslint-disable-next-line no-alert
      if (layer) {
        if (window.confirm(`Save ${layer._tilesforSave.length}`)) {
          successCallback();
        }
      }
    };

    const confirmRemoval = function (layer: any, successCallback: any) {
      if (window.confirm('Remove all the tiles?')) {
        successCallback();
      }
    };

    let control: L.ControlSaveTiles | null;
    if (layer) {
      control = L.control.savetiles(layer, {
        zoomlevels: zoomLevelsStorage(this.map), // optional zoomlevels to save, default current zoomlevel
        confirm,
        confirmRemoval,
        saveText: '<i class="mdi mdi-18px mdi-download" aria-hidden="true" title="Save tiles"></i>',
        rmText: '<i class="mdi mdi-18px mdi-delete" aria-hidden="true"  title="Remove tiles"></i>'
      });

      control.setPosition(this.options.cacheControlPosition ? this.options.cacheControlPosition : 'bottomright');
      if (this.options.cacheControl) control.addTo(this.map);
    }

    if (this.options.locationControl) {
      L.control
        .locate({
          icon: 'mdi mdi-map-marker',
          iconLoading: 'mdi mdi-map-marker-outline',
          position: this.options.locationControlPosition ? this.options.locationControlPosition : 'bottomright'
        })
        .addTo(this.map);
    }

    this.map.on('zoom', () => {
      if (!this.options.cacheControl) return;
      if (this.map == null) return;
      const zoomLevel = this.map.getZoom();
      if (control) {
        if (zoomLevel < this.MIN_STORAGE_ZOOM_LEVEL || zoomLevel > this.MAX_STORAGE_ZOOM_LEVEL) {
          control.remove();
        } else {
          control.addTo(this.map as L.Map);
          control.setOption('zoomlevels', zoomLevelsStorage(this.map as L.Map));
        }
      }
    });
  }

  private AddSelectedListener() {
    this.map?.off('click');
    if (this.map != null) {
      this.map.on('click', () => {
        const route = this.$route.name;
        if (!this.machineStore.isRecording && route == 'Home') {
          this.machineStore.setSelectedMachine(null);
          this.machineStore.setSelectedMachineIndex(null);
          this.menuStore.resetMachineMenuState();
          this.missionStore.selectMission(null);
          this.missionStore.setSelectedMissionIndex(null);
        }
      });
    }
  }

  destroyed() {
    this.map?.off('click');
  }

  mounted() {
    this.initializeMap();
    this.AddSelectedListener();

    this.$emit('map:loaded', this.map);
  }
}
